import React, { useState, useCallback } from "react";
import cx from "classnames";
import { noop } from "lodash";

import NxDataTable, { Column } from "@nextiva/crm-datatable";

import Styles from "./DataTableBody.scss";
import { Tooltip } from "@nextiva/next-ui";

export interface DisplayPreference {
  name: string;
  label?: string;
  type?: string;
  width?: number;
  display?: boolean;
  resizable?: boolean;
  options?: unknown[];
  /** Sort flag as crm-datatable names it */
  shouldRenderSort?: boolean;
  /** Sort flag as the ReportCallLog data provider names it */
  sortable?: boolean;
  shouldRenderFilter?: boolean;
}

export interface RowData {
  rowOptions?: {
    disabled?: boolean;
  };
  [key: string]: unknown;
}

export interface SortData {
  key?: string;
  order?: string;
}

export interface DataTableBodyProps {
  isLoading?: boolean;
  data?: RowData[];
  displayPreferences?: DisplayPreference[];
  filterData?: Record<string, unknown> | null;
  sortData?: SortData | null;
  onRowClick?: (e: React.MouseEvent, item: RowData) => void;
  /** Returns a custom cell renderer for the named column, if any */
  getRenderer?: (name: string) => unknown;
  cellRenderer?: unknown; // not used
  onFiltersChange?: (filterData: Record<string, unknown>) => void;
  onSortChange?: (sortData: SortData) => void;
  disabledRowTooltipMessage?: string | null;
  className?: string;
  renderActions?: unknown;
  "data-testid"?: string;
}

const DataTableBody = ({
  isLoading = false,
  data = [],
  displayPreferences = [],
  filterData = null,
  sortData = null,
  onRowClick = noop,
  getRenderer = noop,
  cellRenderer = noop, // not used
  onFiltersChange = noop,
  onSortChange = noop,
  disabledRowTooltipMessage = null,
  className,
  renderActions,
  "data-testid": dataTestId = "AnalyticsSpa-DataTableBody"
}: DataTableBodyProps) => {
  const [isTooltipOpened, setIsTooltipOpened] = useState(false);

  const handleSelectRow = useCallback(
    (e: React.MouseEvent, item: RowData) => {
      if (item.rowOptions && item.rowOptions.disabled) {
        setIsTooltipOpened(true);
      } else {
        onRowClick(e, item);
      }
    },
    [onRowClick]
  );

  return (
    <div className={cx(Styles.body, className)}>
      <NxDataTable
        isLoading={isLoading}
        data={data}
        renderActions={renderActions}
        displayPreferences={displayPreferences}
        onSortChange={onSortChange}
        onFiltersChange={onFiltersChange}
        onRowClick={handleSelectRow}
        checkable={false}
        disableGear
        data-testid={dataTestId}
      >
        {displayPreferences
          .filter(p => p.display)
          .map(item => {
            const customRenderer = getRenderer?.(item.name);
            const columnProps = {
              name: item.name,
              type: item.type,
              width: item.width,
              resizable: item.resizable,
              options: item.options || [],
              isFixedFilter: !!item.options,
              getCellData: (rowData: RowData, name: string) => rowData[name],
              // Providers name this flag either way; ReportCallLog emits `sortable`.
              shouldRenderSort: !!(item.shouldRenderSort ?? item.sortable),
              shouldRenderFilter: !!item.shouldRenderFilter,
              cellRenderer: customRenderer,
              filterData: filterData?.[item.name],
              order: sortData?.key === item.name ? sortData.order : undefined,
              draggableOpts: {
                enableUserSelectHack: false
              }
            };

            return (
              <Column key={item.name} {...columnProps}>
                {item.label}
              </Column>
            );
          })}
      </NxDataTable>

      {disabledRowTooltipMessage && (
        <Tooltip
          dark
          placement="bottom"
          text={disabledRowTooltipMessage}
          isOpen={isTooltipOpened}
          popoverClassName={Styles.tablePopover}
        />
      )}
    </div>
  );
};

export default DataTableBody;
